import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, makeStyles, Slider, Spinner, Text, tokens } from "@fluentui/react-components";
import { DocumentText20Filled, Warning24Filled } from "@fluentui/react-icons";
import { Modal } from "../modal/Modal";
import { bytesToSize } from "../../utils/bytes-to-size";
import { loadModelInfo } from "../../utils/load-model-info";
import { calculateModelOptimizationInfo } from "../../utils/model-optimization";
import { AssetOptimizationService } from "../../api/AssetOptimizationService";
import AvatarPreview from "../avatar-preview";
import { FormattedMessage, useIntl } from "react-intl";
import InfoButton from "../fluent/components/InfoButton";
import ConfirmOptimizationDialog from "./ConfirmOptimizationDialog";
import notificationsCenter from "../../singleton/NotificationsCenter";
import { LogMessageType } from "../../utils/messages";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalM
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS
  },
  metrics: {
    display: "flex",
    gap: "10px",
    justifyContent: "space-between",
    flexWrap: "wrap"
  },
  metricCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS
  },
  metricLabel: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3
  },
  sliderSection: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS
  },
  sliderHeader: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center"
  },
  sliderLabels: {
    display: "flex",
    justifyContent: "space-between",
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200
  },
  presetDetails: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    marginBottom: tokens.spacingVerticalL
  },
  warningBox: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    color: "#f2994a"
  },
  errorBox: {
    marginTop: tokens.spacingVerticalL
  },
  previewWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    marginBottom: tokens.spacingVerticalL
  },
  previewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "4px"
  },
  previewStat: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS
  },
  positiveText: {
    color: tokens.colorStatusSuccessForeground1
  },
  negativeText: {
    color: "var(--color-danger)"
  },
  previewContainer: {
    height: "360px",
    "& > div": {
      height: "100%"
    }
  },
  spinnerRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    margin: "0 auto"
  },
  scoreLow: {
    color: tokens.colorStatusSuccessForeground1
  },
  scoreMedium: {
    color: "#FFC107"
  },
  scoreHigh: {
    color: tokens.colorStatusDangerForeground1
  },
  slider: {
    width: "100%"
  },
  descriptionText: {
    color: tokens.colorNeutralForeground3
  },
  presetDescriptionText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200
  },
  previewBackdrop: {
    backdropFilter: "blur(10px)"
  }
});

const OPTIMIZATION_PRESETS = [
  {
    value: 0,
    name: "maximum",
    label: "Maximum Performance",
    description: "256px textures, high mesh simplification"
  },
  {
    value: 1,
    name: "balanced",
    label: "Balanced",
    description: "512px textures, moderate mesh simplification"
  },
  {
    value: 2,
    name: "quality",
    label: "High Quality",
    description: "1024px textures, light mesh simplification"
  },
  {
    value: 3,
    name: "minimal",
    label: "Maximum Quality",
    description: "2048px textures, no mesh simplification"
  }
];

const getAssetIdentifier = asset => asset?.id ?? asset?.uuid ?? asset?.assetId;
const getScoreClass = (stylesRef, score) => {
  if (score === "Low") {
    return stylesRef.scoreLow;
  }
  if (score === "Medium") {
    return stylesRef.scoreMedium;
  }
  if (score === "High") {
    return stylesRef.scoreHigh;
  }
  return undefined;
};

export function Optimize3DModelDialog({ asset, open, onOptimized, onClose, isFromUpload = false }) {
  const styles = useStyles();
  const intl = useIntl();
  const optimizationServiceRef = useRef(new AssetOptimizationService());
  const sceneRenderSuppressedRef = useRef(false);
  const backdropClassRef = useRef(null);
  const previewBackdropTokens = useMemo(
    () => styles.previewBackdrop.split(" ").filter(Boolean),
    [styles.previewBackdrop]
  );

  const [sliderValue, setSliderValue] = useState(1);
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [phase, setPhase] = useState("config");
  const [errorMessage, setErrorMessage] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [optimizedUrl, setOptimizedUrl] = useState(null);
  const [optimizedSize, setOptimizedSize] = useState(0);
  const [contentType, setContentType] = useState("model/gltf-binary");
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const currentPreset = useMemo(() => {
    return OPTIMIZATION_PRESETS[sliderValue] || OPTIMIZATION_PRESETS[1];
  }, [sliderValue]);

  const formattedTriangles = metrics?.triangles !== undefined ? intl.formatNumber(metrics.triangles) : null;

  useEffect(() => {
    if (!open) {
      return;
    }

    setSliderValue(1);
    setMetrics(null);
    setPhase("config");
    setErrorMessage(null);
    setIsOptimizing(false);
    setJobId(null);
    setContentType("model/gltf-binary");
    setIsConfirmDialogOpen(false);
    setOptimizedUrl(previousUrl => {
      if (previousUrl) {
        optimizationServiceRef.current.revokeBlobUrl(previousUrl);
      }
      return null;
    });
    setOptimizedSize(0);
  }, [open, asset?.id, asset?.uuid]);

  useEffect(() => {
    if (!open || !asset?.url) {
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setErrorMessage(null);

    loadModelInfo(asset.url)
      .then(stats => {
        if (!isMounted) {
          return;
        }
        const info = calculateModelOptimizationInfo(stats, asset.size);
        setMetrics({
          triangles: info.triangles,
          densityScore: info.densityScore,
          sizeScore: info.sizeScore,
          hasWarnings: info.hasWarnings,
          readableSize: info.readableSize || bytesToSize(asset.size || stats.totalSize || 0)
        });
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }
        const fallback = calculateModelOptimizationInfo({ triangles: 0, totalSize: asset.size }, asset.size);
        setMetrics({
          triangles: fallback.triangles,
          densityScore: fallback.densityScore,
          sizeScore: fallback.sizeScore,
          hasWarnings: fallback.hasWarnings,
          readableSize: bytesToSize(asset.size || 0)
        });
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [open, asset]);

  useEffect(() => {
    const service = optimizationServiceRef.current;
    return () => {
      service.revokeBlobUrl(optimizedUrl);
    };
  }, [optimizedUrl]);

  useEffect(() => {
    if (phase === "preview") {
      if (window.APP?.setMainSceneRenderSuppressed && !sceneRenderSuppressedRef.current) {
        window.APP.setMainSceneRenderSuppressed(true, "optimize-3d-model-preview");
        sceneRenderSuppressedRef.current = true;
      }
    } else if (sceneRenderSuppressedRef.current && window.APP?.setMainSceneRenderSuppressed) {
      window.APP.setMainSceneRenderSuppressed(false, "optimize-3d-model-preview");
      sceneRenderSuppressedRef.current = false;
    }
  }, [phase]);

  useEffect(() => {
    return () => {
      if (sceneRenderSuppressedRef.current && window.APP?.setMainSceneRenderSuppressed) {
        window.APP.setMainSceneRenderSuppressed(false, "optimize-3d-model-preview");
        sceneRenderSuppressedRef.current = false;
      }
    };
  }, []);

  useEffect(() => {
    const dialogSurface = document.getElementById("optimize-3d-model-dialog");
    const overlayRoot = dialogSurface?.parentElement?.parentElement;
    const backdrop = overlayRoot?.querySelector(".fui-DialogSurface__backdrop");

    if (phase === "preview" && open && backdrop) {
      previewBackdropTokens.forEach(token => backdrop.classList.add(token));
      backdropClassRef.current = { element: backdrop, tokens: previewBackdropTokens };
    } else {
      const targetEntry = backdropClassRef.current;
      const targetElement = backdrop ?? targetEntry?.element;
      const tokensToRemove = targetEntry?.tokens ?? previewBackdropTokens;
      if (targetElement) {
        tokensToRemove.forEach(token => targetElement.classList.remove(token));
      }
      if (backdropClassRef.current && targetElement === backdropClassRef.current.element) {
        backdropClassRef.current = null;
      }
    }

    return () => {
      if (backdropClassRef.current) {
        backdropClassRef.current.tokens.forEach(token => backdropClassRef.current.element.classList.remove(token));
        backdropClassRef.current = null;
      }
    };
  }, [phase, open, previewBackdropTokens]);

  if (!asset) {
    return null;
  }

  const handleClose = () => {
    if (jobId) {
      optimizationServiceRef.current.cancel(jobId).catch(() => {});
    }
    setPhase("config");
    setJobId(null);
    setIsConfirmDialogOpen(false);
    optimizationServiceRef.current.revokeBlobUrl(optimizedUrl);
    setOptimizedUrl(null);
    onClose?.();
  };

  const handleOpenConfirmDialog = () => {
    setIsConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleOptimize = async () => {
    const assetIdentifier = getAssetIdentifier(asset);
    if (!assetIdentifier) {
      setErrorMessage(
        intl.formatMessage({
          id: "optimize3d.error.assetIdMissing",
          defaultMessage: "Asset identifier not found."
        })
      );
      return;
    }

    setIsOptimizing(true);
    setErrorMessage(null);

    try {
      const service = optimizationServiceRef.current;
      const result = await service.optimize(assetIdentifier, currentPreset.name);

      if (result.status === "completed") {
        optimizationServiceRef.current.revokeBlobUrl(optimizedUrl);
        setOptimizedUrl(result.blobUrl || null);
        setOptimizedSize(result.size);
        setContentType(result.contentType || "model/gltf-binary");
        setPhase("preview");
        return;
      }

      if (result.status === "processing") {
        setJobId(result.jobId);
        const completion = await service.pollForCompletion(result.jobId, result.preset);
        setOptimizedSize(completion.size);
        setContentType(completion.contentType || "model/gltf-binary");
        setOptimizedUrl(completion.tempAzureUrl);
        setPhase("preview");
      }
    } catch (error) {
      setErrorMessage(
        error.message ||
          intl.formatMessage({
            id: "optimize3d.error.optimizeFailed",
            defaultMessage: "Failed to optimize the model."
          })
      );
    } finally {
      setIsOptimizing(false);
    }
  };

  const confirmOptimization = async createCopy => {
    const assetIdentifier = getAssetIdentifier(asset);
    if (!assetIdentifier) {
      setErrorMessage(
        intl.formatMessage({
          id: "optimize3d.error.assetIdMissing",
          defaultMessage: "Asset identifier not found."
        })
      );
      return;
    }

    if (!optimizedUrl) {
      setErrorMessage(
        intl.formatMessage({
          id: "optimize3d.error.noOptimizedFile",
          defaultMessage: "No optimized file is available."
        })
      );
      return;
    }

    if (!jobId) {
      setErrorMessage(
        intl.formatMessage({
          id: "optimize3d.error.jobMissing",
          defaultMessage: "Optimization job was not found. Please run the optimization again."
        })
      );
      return;
    }

    setIsOptimizing(true);
    setErrorMessage(null);

    try {
      const service = optimizationServiceRef.current;
      const response = await service.confirm(
        jobId,
        assetIdentifier,
        currentPreset.name,
        optimizedSize,
        contentType,
        createCopy,
        asset.collectionId
      );

      onOptimized?.(response, { isCopy: createCopy });
      notificationsCenter.success(LogMessageType.assetOptimized);
      handleClose();
    } catch (error) {
      if (error.status === 402) {
        setErrorMessage(
          intl.formatMessage({
            id: "optimize3d.error.storageLimit",
            defaultMessage: "Storage limit reached. Upgrade your plan to continue."
          })
        );
        if (window.APP?.scene?.emit) {
          window.APP.scene.emit("storage_limit_reached");
        }
        return;
      }
      setErrorMessage(
        error.message ||
          intl.formatMessage({
            id: "optimize3d.error.applyFailed",
            defaultMessage: "Failed to apply the optimization."
          })
      );
    } finally {
      setIsOptimizing(false);
    }
  };

  const metricsContent = isLoading ? (
    <div className={styles.spinnerRow}>
      <Spinner size="tiny" />
      <Text>
        <FormattedMessage id="optimize3d.loading" defaultMessage="Analyzing model…" />
      </Text>
    </div>
  ) : (
    <div className={styles.metrics}>
      <div className={styles.metricCard}>
        <div className={styles.metricLabel}>
          <Text weight="semibold">
            <FormattedMessage id="optimize3d.metric.density" defaultMessage="Density:" />
          </Text>
        </div>
        <Text weight="semibold" className={getScoreClass(styles, metrics?.densityScore)}>
          {formattedTriangles ? (
            <FormattedMessage
              id="optimize3d.metric.densityValue"
              defaultMessage="{count} triangles"
              values={{ count: formattedTriangles }}
            />
          ) : (
            "—"
          )}
        </Text>
        <InfoButton
          info={intl.formatMessage({
            id: "optimize3d.metric.density.help",
            defaultMessage: "The number of polygons (triangles) in the model. Lower values perform better."
          })}
        />
      </div>
      <div className={styles.metricCard}>
        <div className={styles.metricLabel}>
          <Text weight="semibold">
            <FormattedMessage id="optimize3d.metric.size" defaultMessage="Size:" />
          </Text>
        </div>
        <Text weight="semibold" className={getScoreClass(styles, metrics?.sizeScore)}>
          {metrics?.readableSize || bytesToSize(asset.size || 0)}
        </Text>
        <InfoButton
          info={intl.formatMessage({
            id: "optimize3d.metric.size.help",
            defaultMessage:
              "The total file size of the model including textures and buffers. Smaller files load faster."
          })}
        />
      </div>
    </div>
  );

  const renderConfigContent = () => (
    <>
      <div className={styles.header}>
        <div className={styles.fileRow}>
          <DocumentText20Filled />
          <Text weight="semibold">
            {asset?.title || intl.formatMessage({ id: "optimize3d.defaultTitle", defaultMessage: "3D model" })}
          </Text>
        </div>
        <Text className={styles.descriptionText}>
          <FormattedMessage
            id="optimize3d.description"
            defaultMessage="Optimize your 3D model to reduce file size and improve loading performance."
          />
        </Text>
      </div>

      {metrics?.hasWarnings && (
        <div className={styles.warningBox} role="status">
          <Warning24Filled />
          <Text>
            <FormattedMessage
              id="optimize3d.warning.heavyModel"
              defaultMessage="We detected a large model. We recommend optimizing it to improve performance."
            />
          </Text>
        </div>
      )}

      {metricsContent}

      <div className={styles.sliderSection}>
        <div>
          <Slider
            min={0}
            max={OPTIMIZATION_PRESETS.length - 1}
            value={sliderValue}
            step={1}
            onChange={(_, data) => setSliderValue(data.value)}
            aria-label={intl.formatMessage({
              id: "optimize3d.slider.aria",
              defaultMessage: "Optimization level"
            })}
            disabled={isOptimizing}
            className={styles.slider}
          />
          <div className={styles.sliderLabels}>
            <Text>
              <FormattedMessage id="optimize3d.slider.performance" defaultMessage="Performance" />
            </Text>
            <Text>
              <FormattedMessage id="optimize3d.slider.quality" defaultMessage="Quality" />
            </Text>
          </div>
        </div>

        <div className={styles.sliderHeader}>
          <div className={styles.presetDetails}>
            <Text weight="semibold">{currentPreset.label}</Text>
            <Text className={styles.presetDescriptionText}>{currentPreset.description}</Text>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className={styles.errorBox}>
          <Text>{errorMessage}</Text>
        </div>
      )}
    </>
  );

  const renderPreviewContent = () => {
    const originalSize = asset.size || 0;
    const difference = originalSize - optimizedSize;
    const percentage = originalSize > 0 ? Math.round((difference / originalSize) * 100) : 0;
    const isHeavier = difference < 0;

    return (
      <div className={styles.previewWrapper}>
        <div className={styles.descriptionText}>
          <FormattedMessage
            id="optimize3d.preview.description"
            defaultMessage="Review the optimized model below to see the results of the optimization."
          />
        </div>
        <div className={styles.previewHeader}>
          <div className={styles.previewStat}>
            <Text className={styles.metricLabel}>
              <FormattedMessage id="optimize3d.preview.original" defaultMessage="Original" />
            </Text>
            <Text weight="semibold">{bytesToSize(originalSize)}</Text>
          </div>
          <div className={styles.previewStat}>
            <Text className={styles.metricLabel}>
              <FormattedMessage id="optimize3d.preview.optimized" defaultMessage="Optimized" />
            </Text>
            <Text weight="semibold" className={isHeavier ? styles.negativeText : styles.positiveText}>
              {bytesToSize(optimizedSize)}
            </Text>
          </div>
          <div className={styles.previewStat}>
            <Text className={styles.metricLabel}>
              {isHeavier ? (
                <FormattedMessage id="optimize3d.preview.increase" defaultMessage="Increase" />
              ) : (
                <FormattedMessage id="optimize3d.preview.savings" defaultMessage="Savings" />
              )}
            </Text>
            <Text weight="semibold" className={isHeavier ? styles.negativeText : styles.positiveText}>
              {`${isHeavier ? "+" : ""}${Math.abs(percentage)}%`}
            </Text>
          </div>
        </div>

        <div className={styles.previewContainer}>
          <AvatarPreview
            key={optimizedUrl}
            avatarGltfUrl={optimizedUrl}
            contentType="model"
            isAvatarListing={false}
            hasError={!optimizedUrl}
            aria-label={intl.formatMessage({
              id: "optimize3d.preview.ariaLabel",
              defaultMessage: "Optimized model preview"
            })}
          />
        </div>

        {errorMessage && (
          <div className={styles.errorBox}>
            <Text>{errorMessage}</Text>
          </div>
        )}
      </div>
    );
  };

  const actions =
    phase === "config" ? (
      <>
        <Button
          appearance="secondary"
          onClick={handleClose}
          aria-label={
            isFromUpload
              ? intl.formatMessage({
                  id: "optimize3d.actions.skip.aria",
                  defaultMessage: "Skip optimization"
                })
              : intl.formatMessage({
                  id: "optimize3d.actions.cancel.aria",
                  defaultMessage: "Cancel optimization"
                })
          }
        >
          {isFromUpload ? (
            <FormattedMessage id="optimize3d.actions.skip" defaultMessage="Skip" />
          ) : (
            <FormattedMessage id="optimize3d.actions.cancel" defaultMessage="Cancel" />
          )}
        </Button>
        <Button
          appearance="primary"
          onClick={handleOptimize}
          disabled={isOptimizing || isLoading}
          aria-label={intl.formatMessage({
            id: "optimize3d.actions.optimize.aria",
            defaultMessage: "Optimize model"
          })}
          icon={isOptimizing ? <Spinner size="tiny" /> : null}
        >
          {isOptimizing ? (
            <FormattedMessage id="optimize3d.actions.optimizing" defaultMessage="Optimizing…" />
          ) : (
            <FormattedMessage id="optimize3d.actions.optimize" defaultMessage="Optimize" />
          )}
        </Button>
      </>
    ) : (
      <>
        <Button
          appearance="secondary"
          onClick={() => setPhase("config")}
          disabled={isOptimizing}
          aria-label={intl.formatMessage({
            id: "optimize3d.actions.back.aria",
            defaultMessage: "Go back"
          })}
        >
          <FormattedMessage id="optimize3d.actions.back" defaultMessage="Back" />
        </Button>
        <Button
          appearance="primary"
          onClick={handleOpenConfirmDialog}
          disabled={isOptimizing}
          aria-label={intl.formatMessage({
            id: "optimize3d.actions.apply.aria",
            defaultMessage: "Apply optimization"
          })}
        >
          <FormattedMessage id="optimize3d.actions.apply" defaultMessage="Apply optimization" />
        </Button>
      </>
    );

  return (
    <>
      <Modal
        id="optimize-3d-model-dialog"
        title={<FormattedMessage id="optimize3d.title" defaultMessage="Optimize 3D model" />}
        open={open}
        onOpenChange={(_, data) => {
          if (!data.open) {
            handleClose();
          }
        }}
        onClose={handleClose}
        contentClassName={styles.root}
        actions={actions}
      >
        {phase === "config" ? renderConfigContent() : renderPreviewContent()}
      </Modal>
      {isConfirmDialogOpen && (
        <ConfirmOptimizationDialog
          fileName={
            asset?.title ||
            asset?.name ||
            intl.formatMessage({ id: "optimize3d.defaultTitle", defaultMessage: "3D model" })
          }
          onConfirmReplace={() => confirmOptimization(false)}
          onConfirmCopy={() => confirmOptimization(true)}
          onCancel={handleCloseConfirmDialog}
          hideDialog={handleCloseConfirmDialog}
        />
      )}
    </>
  );
}

Optimize3DModelDialog.propTypes = {
  asset: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    uuid: PropTypes.string,
    assetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    name: PropTypes.string,
    size: PropTypes.number,
    url: PropTypes.string,
    collectionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  open: PropTypes.bool,
  onOptimized: PropTypes.func,
  onClose: PropTypes.func,
  isFromUpload: PropTypes.bool
};
